import { useState } from 'react';
import { Image as ImageIcon, Eye, AlertTriangle, Trash2, ChevronLeft, ChevronRight, Globe, Code, Search, Heart, EyeIcon, ImagePlus, Wand2, User, Layers, Flag } from 'lucide-react';
import type { ImageJob } from './types';
import type { PaginationInfo } from '../../services/api';
import ImageDetailModal from './ImageDetailModal';
import { LazyImage } from '../LazyImage';
import { formatBeijingDate, formatBeijingDateTime } from '../../utils/time';

function formatMode(mode?: string): { label: string; icon: React.ReactNode; color: string } {
  switch (mode) {
    case 'text2img':
      return { label: '文生图', icon: <Wand2 className="w-3 h-3" />, color: 'bg-blue-100 text-blue-600' };
    case 'img2img':
      return { label: '图生图', icon: <ImagePlus className="w-3 h-3" />, color: 'bg-purple-100 text-purple-600' };
    case 'inpaint':
      return { label: '局部重绘', icon: <Layers className="w-3 h-3" />, color: 'bg-orange-100 text-orange-600' };
    default:
      return { label: '文生图', icon: <Wand2 className="w-3 h-3" />, color: 'bg-gray-100 text-gray-600' };
  }
}

function formatQuality(quality?: string): string {
  switch (quality) {
    case 'low': return '标准';
    case 'medium': return '高清';
    case 'high': return '超清';
    default: return quality || '-';
  }
}

function getFallbackInfo(fallbackTrace?: string | null): { label: string; summary: string; tone: string } | null {
  if (!fallbackTrace) {
    return null;
  }

  try {
    const parsed = JSON.parse(fallbackTrace) as {
      recovered?: boolean;
      attempts?: Array<{ channelName?: string | null }>;
      firstChannel?: { channelName?: string | null };
      successChannel?: { channelName?: string | null };
    };
    const firstChannelName = parsed.firstChannel?.channelName || parsed.attempts?.[0]?.channelName || '首层渠道';
    const successChannelName = parsed.successChannel?.channelName;

    if (parsed.recovered) {
      return {
        label: '已降级恢复',
        summary: successChannelName ? `${firstChannelName} -> ${successChannelName}` : `${firstChannelName} -> 已恢复`,
        tone: 'bg-amber-100 text-amber-700',
      };
    }

    return {
      label: '降级失败',
      summary: `${firstChannelName} -> 最终失败`,
      tone: 'bg-red-100 text-red-700',
    };
  } catch {
    return {
      label: '存在降级',
      summary: '查看详情',
      tone: 'bg-gray-100 text-gray-700',
    };
  }
}

export interface ImageFilters {
  userEmail: string;
  status: string;
  model: string;
  source: string;
  mode: string;
  startDate: string;
  endDate: string;
  search: string;
}

interface ImagesTabProps {
  images: ImageJob[];
  previewImage: ImageJob | null;
  pagination: PaginationInfo;
  loading: boolean;
  filters: ImageFilters;
  onPreview: (img: ImageJob) => void;
  onClosePreview: () => void;
  onPageChange: (page: number) => void;
  onDeleteImages: (ids: string[]) => void;
  onFilterChange: (filters: ImageFilters) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
}

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'completed', label: '完成' },
  { value: 'processing', label: '处理中' },
  { value: 'failed', label: '失败' },
];

const SOURCE_OPTIONS = [
  { value: '', label: '全部来源' },
  { value: 'web', label: '网页' },
  { value: 'api', label: 'API' },
];

const MODE_OPTIONS = [
  { value: '', label: '全部模式' },
  { value: 'text2img', label: '文生图' },
  { value: 'img2img', label: '图生图' },
  { value: 'inpaint', label: '局部重绘' },
];

export default function ImagesTab({
  images,
  previewImage,
  pagination,
  loading,
  filters,
  onPreview,
  onClosePreview,
  onPageChange,
  onDeleteImages,
  onFilterChange,
  onApplyFilters,
  onResetFilters,
}: ImagesTabProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === images.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(images.map((img) => img.id)));
    }
  };

  const handleDelete = () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`确定要删除选中的 ${selectedIds.size} 条图片记录吗？软删除：仅隐藏记录，文件保留，可恢复。`)) return;
    onDeleteImages(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleFilterChange = (key: keyof ImageFilters, value: string) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const pageNumbers = Array.from({ length: pagination.totalPages }, (_, i) => i + 1);
  const showPages = pageNumbers.filter((p) => {
    if (pagination.totalPages <= 7) return true;
    if (p <= 3 || p > pagination.totalPages - 3) return true;
    if (Math.abs(p - pagination.page) <= 1) return true;
    return false;
  });

  const hasActiveFilters = filters.userEmail || filters.status || filters.model || 
    filters.source || filters.mode || filters.startDate || filters.endDate || filters.search;

  return (
    <div className="space-y-4">
      {/* Filter Section - Always Visible */}
      <div className="bg-white rounded-xl shadow-sm border border-light-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          {/* Search by prompt */}
          <div className="relative xl:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-muted" />
            <input
              type="text"
              placeholder="搜索提示词..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue"
            />
          </div>

          {/* User Email */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-muted" />
            <input
              type="text"
              placeholder="用户邮箱..."
              value={filters.userEmail}
              onChange={(e) => handleFilterChange('userEmail', e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue"
            />
          </div>

          {/* Status */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue bg-white"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Source */}
          <select
            value={filters.source}
            onChange={(e) => handleFilterChange('source', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue bg-white"
          >
            {SOURCE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Mode */}
          <select
            value={filters.mode}
            onChange={(e) => handleFilterChange('mode', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue bg-white"
          >
            {MODE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Model */}
          <input
            type="text"
            placeholder="模型..."
            value={filters.model}
            onChange={(e) => handleFilterChange('model', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue"
          />

          {/* Start Date */}
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue"
          />

          {/* End Date */}
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-light-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue/20 focus:border-accent-blue"
          />
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-light-border">
          <div className="flex items-center gap-2">
            <button
              onClick={onApplyFilters}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-accent-blue rounded-lg hover:bg-accent-blue/90 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Search className="w-3.5 h-3.5" />
              )}
              查询
            </button>
            {hasActiveFilters && (
              <button
                onClick={onResetFilters}
                className="px-3 py-1.5 text-sm text-light-muted hover:text-red-500 transition-colors"
              >
                重置
              </button>
            )}
          </div>
          
          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleSelectAll}
              className="px-3 py-1.5 text-sm text-light-text bg-light-hover rounded-lg hover:bg-light-border transition-colors"
            >
              {selectedIds.size === images.length && images.length > 0 ? '取消全选' : '全选'}
            </button>
            {selectedIds.size > 0 && (
              <>
                <span className="text-sm text-light-muted">
                  已选 {selectedIds.size} 条
                </span>
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  删除
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Images Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-light-border overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden xl:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === images.length && images.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 w-14">图片</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 min-w-[200px]">提示词</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 w-[180px]">用户</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">模型</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">尺寸</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">模式</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">质量</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">状态</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">降级</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">来源</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">积分</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">迭代</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">互动</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {images.map((img) => {
                const modeInfo = formatMode(img.mode);
                const fallbackInfo = getFallbackInfo(img.fallbackTrace);
                return (
                  <tr key={img.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(img.id)}
                        onChange={() => toggleSelect(img.id)}
                        className="w-4 h-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {img.resultUrl ? (
                        <button
                          onClick={() => onPreview(img)}
                          className="relative w-10 h-10 rounded-lg overflow-hidden border border-gray-200 hover:border-accent-blue transition-colors group"
                        >
                          <LazyImage
                            work={img}
                            size="small"
                            alt={img.prompt}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <Eye className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      ) : (
                        <button
                          onClick={() => onPreview(img)}
                          className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                          title="查看详细信息"
                        >
                          <ImageIcon className="w-4 h-4 text-gray-400" />
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-xs">
                        <p className="text-gray-700 truncate" title={img.prompt}>{img.prompt}</p>
                        {img.errorMessage && (
                          <details className="mt-1">
                            <summary className="flex items-center gap-1 text-xs text-red-500 cursor-pointer">
                              <AlertTriangle className="w-3 h-3" />
                              <span className="truncate">{
                                (() => {
                                  try {
                                    const parsed = JSON.parse(img.errorMessage!);
                                    const msg = typeof parsed.message === 'string' ? parsed.message : '生成失败';
                                    return parsed.httpStatus ? `${msg} (${parsed.httpStatus})` : msg;
                                  } catch {
                                    return img.errorMessage!.length > 36 ? `${img.errorMessage!.slice(0, 36)}...` : img.errorMessage!;
                                  }
                                })()
                              }</span>
                            </summary>
                            <div className="mt-1 p-2 bg-red-50 rounded text-xs text-red-700">
                              <pre className="whitespace-pre-wrap break-words">{
                                (() => {
                                  try {
                                    return JSON.stringify(JSON.parse(img.errorMessage!), null, 2);
                                  } catch {
                                    return img.errorMessage!;
                                  }
                                })()
                              }</pre>
                            </div>
                          </details>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-[180px]">
                        <div className="truncate text-gray-700" title={img.userEmail}>{img.userEmail}</div>
                        {img.userName && (
                          <div className="truncate text-xs text-gray-400" title={img.userName}>
                            {img.userName}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-gray-700 text-xs bg-gray-100 px-2 py-0.5 rounded">{img.model}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-gray-600 text-xs">
                        {img.size || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${modeInfo.color}`}>
                        {modeInfo.icon}
                        {modeInfo.label}
                      </span>
                      {(img.hasReferenceImages || img.hasMaskImage) && (
                        <div className="flex gap-1 mt-1">
                          {img.hasReferenceImages && <span className="text-[10px] bg-blue-50 text-blue-500 px-1 rounded">参考</span>}
                          {img.hasMaskImage && <span className="text-[10px] bg-orange-50 text-orange-500 px-1 rounded">蒙版</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-gray-600 text-xs">{formatQuality(img.quality)}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                        img.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : img.status === 'failed'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {img.status === 'completed' ? '完成' : img.status === 'failed' ? '失败' : '处理中'}
                      </span>
                      {img.isHidden && <span className="ml-1 text-[10px] bg-gray-100 text-gray-500 px-1 rounded">隐藏</span>}
                    </td>
                    <td className="px-3 py-2">
                      {fallbackInfo ? (
                        <div className="min-w-[120px]">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${fallbackInfo.tone}`}>
                            {fallbackInfo.label}
                          </span>
                          <div className="mt-1 text-[11px] text-gray-500 truncate" title={fallbackInfo.summary}>
                            {fallbackInfo.summary}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                        img.source === 'api'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {img.source === 'api' ? <Code className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                        {img.source === 'api' ? 'API' : '网页'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-gray-700 font-medium">{img.creditsUsed}</span>
                    </td>
                    <td className="px-3 py-2">
                      {img.generation && img.generation > 1 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-orange-600">
                          <Layers className="w-3 h-3" />
                          #{img.generation}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {img.isPublic && (
                          <span className="flex items-center gap-0.5" title="公开">
                            <EyeIcon className="w-3 h-3" />
                            {img.viewCount || 0}
                          </span>
                        )}
                        <span className="flex items-center gap-0.5" title="点赞">
                          <Heart className={`w-3 h-3 ${img.isFavorite ? 'fill-red-400 text-red-400' : ''}`} />
                          {img.likeCount || 0}
                        </span>
                        {img.reportCount ? (
                          <span className="flex items-center gap-0.5 text-red-500" title="举报">
                            <Flag className="w-3 h-3" />
                            {img.reportCount}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-gray-500">
                        {formatBeijingDate(img.createdAt)}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {new Date(img.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="xl:hidden divide-y divide-gray-100">
          {images.map((img) => {
            const modeInfo = formatMode(img.mode);
            const fallbackInfo = getFallbackInfo(img.fallbackTrace);
            return (
              <div key={img.id} className="p-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(img.id)}
                    onChange={() => toggleSelect(img.id)}
                    className="w-4 h-4 mt-1 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                  />
                  {img.resultUrl ? (
                    <button
                      onClick={() => onPreview(img)}
                      className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 shrink-0"
                    >
                      <LazyImage work={img} size="small" alt={img.prompt} className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <button
                      onClick={() => onPreview(img)}
                      className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-200 transition-colors"
                      title="查看详细信息"
                    >
                      <ImageIcon className="w-6 h-6 text-gray-400" />
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 line-clamp-2 mb-1">{img.prompt}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${modeInfo.color}`}>
                        {modeInfo.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full ${
                        img.status === 'completed' ? 'bg-green-100 text-green-700' :
                        img.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {img.status === 'completed' ? '完成' : img.status === 'failed' ? '失败' : '处理中'}
                      </span>
                      {fallbackInfo && (
                        <span className={`px-2 py-0.5 rounded-full ${fallbackInfo.tone}`}>
                          {fallbackInfo.label}
                        </span>
                      )}
                      <span className="text-gray-500">{img.model}</span>
                      <span className="text-gray-400">{img.creditsUsed}积分</span>
                    </div>
                    {fallbackInfo && (
                      <div className="mt-1 text-xs text-gray-500 truncate" title={fallbackInfo.summary}>
                        降级: {fallbackInfo.summary}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-400">
                      <div className="truncate" title={img.userEmail}>{img.userEmail}</div>
                      {img.userName && (
                        <div className="truncate" title={img.userName}>{img.userName}</div>
                      )}
                      <div>{formatBeijingDateTime(img.createdAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-100 gap-3">
            <div className="text-sm text-gray-500">
              共 {pagination.total} 条，第 {pagination.page} / {pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              {showPages.map((p, idx) => {
                const prev = showPages[idx - 1];
                const showEllipsis = prev && p - prev > 1;
                return (
                  <div key={p} className="flex items-center gap-1">
                    {showEllipsis && <span className="px-2 text-sm text-gray-400">...</span>}
                    <button
                      onClick={() => onPageChange(p)}
                      disabled={loading}
                      className={`min-w-[2rem] px-2 py-1 text-sm rounded-lg ${
                        p === pagination.page
                          ? 'bg-accent-blue text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  </div>
                );
              })}
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Image Detail Modal */}
      {previewImage && (
        <ImageDetailModal
          image={previewImage}
          onClose={onClosePreview}
        />
      )}
    </div>
  );
}
